/**
 * FikoDimensionEffects - Next-Gen Visual Effects Package
 *
 * Gelişmiş görsel efekt paketi
 * - Glow efektleri (inner, outer, pulsating)
 * - Pulse animasyonları
 * - Trail efektleri
 * - Particle sistemleri
 * - Shader-based post-processing
 * - Holographic efektler
 * - Energy field efektleri
 *
 * @author FikoYT Advanced CAD Team
 * @version 2.0.0
 */

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';

// ============================================================================
// ENUMS & TYPES
// ============================================================================

/**
 * Efekt tipi
 */
export enum EffectType {
    // Glow efektleri
    GLOW_INNER = 'GLOW_INNER',
    GLOW_OUTER = 'GLOW_OUTER',
    GLOW_PULSATING = 'GLOW_PULSATING',
    GLOW_RAINBOW = 'GLOW_RAINBOW',

    // Pulse efektleri
    PULSE_SCALE = 'PULSE_SCALE',
    PULSE_OPACITY = 'PULSE_OPACITY',
    PULSE_COLOR = 'PULSE_COLOR',
    PULSE_BRIGHTNESS = 'PULSE_BRIGHTNESS',

    // Trail efektleri
    TRAIL_FADE = 'TRAIL_FADE',
    TRAIL_PARTICLE = 'TRAIL_PARTICLE',
    TRAIL_RIBBON = 'TRAIL_RIBBON',
    TRAIL_COMET = 'TRAIL_COMET',

    // Particle efektleri
    PARTICLE_SPARK = 'PARTICLE_SPARK',
    PARTICLE_DUST = 'PARTICLE_DUST',
    PARTICLE_FIRE = 'PARTICLE_FIRE',
    PARTICLE_SNOW = 'PARTICLE_SNOW',
    PARTICLE_ENERGY = 'PARTICLE_ENERGY',

    // Holographic efektler
    HOLOGRAPHIC_SCAN = 'HOLOGRAPHIC_SCAN',
    HOLOGRAPHIC_GLITCH = 'HOLOGRAPHIC_GLITCH',
    HOLOGRAPHIC_NOISE = 'HOLOGRAPHIC_NOISE',
    HOLOGRAPHIC_INTERFERENCE = 'HOLOGRAPHIC_INTERFERENCE',

    // Energy field
    ENERGY_FIELD = 'ENERGY_FIELD',
    ENERGY_PLASMA = 'ENERGY_PLASMA',
    ENERGY_ELECTRIC = 'ENERGY_ELECTRIC',
    ENERGY_SHIELD = 'ENERGY_SHIELD',

    // Distortion
    DISTORTION_WAVE = 'DISTORTION_WAVE',
    DISTORTION_RIPPLE = 'DISTORTION_RIPPLE',
    DISTORTION_WARP = 'DISTORTION_WARP',

    // Highlight
    HIGHLIGHT_OUTLINE = 'HIGHLIGHT_OUTLINE',
    HIGHLIGHT_FILL = 'HIGHLIGHT_FILL',
    HIGHLIGHT_XRAY = 'HIGHLIGHT_XRAY'
}

/**
 * Easing fonksiyonu
 */
export enum EasingType {
    LINEAR = 'LINEAR',
    EASE_IN = 'EASE_IN',
    EASE_OUT = 'EASE_OUT',
    EASE_IN_OUT = 'EASE_IN_OUT',
    ELASTIC = 'ELASTIC',
    BOUNCE = 'BOUNCE',
    BACK = 'BACK',
    SINE = 'SINE',
    EXPO = 'EXPO',
    CIRC = 'CIRC'
}

/**
 * Efekt konfigürasyonu
 */
export interface EffectConfig {
    type: EffectType;
    enabled: boolean;

    // Renk
    color: number;
    secondaryColor?: number;
    opacity: number;

    // Animasyon
    duration: number;
    delay: number;
    loop: boolean;
    easing: EasingType;

    // İntensity
    intensity: number;
    minIntensity?: number;
    maxIntensity?: number;

    // Boyut
    scale: number;
    minScale?: number;
    maxScale?: number;

    // Trail
    trailLength?: number;
    trailDecay?: number;

    // Particle
    particleCount?: number;
    particleSize?: number;
    particleSpeed?: number;

    // Glow
    glowRadius?: number;
    glowFalloff?: number;

    // Shader
    customShader?: THREE.ShaderMaterial;
}

/**
 * Efekt durumu
 */
export interface EffectState {
    isPlaying: boolean;
    isPaused: boolean;
    progress: number;
    elapsedTime: number;
}

// ============================================================================
// DEFAULT VALUES
// ============================================================================

const DEFAULT_EFFECT_CONFIG: EffectConfig = {
    type: EffectType.GLOW_OUTER,
    enabled: true,

    color: 0x00ff88,
    opacity: 1,

    duration: 1000,
    delay: 0,
    loop: true,
    easing: EasingType.EASE_IN_OUT,

    intensity: 0.5,
    scale: 1,

    trailLength: 10,
    trailDecay: 0.95,

    particleCount: 100,
    particleSize: 0.02,
    particleSpeed: 1,

    glowRadius: 0.1,
    glowFalloff: 2
};

// ============================================================================
// SHADER DEFINITIONS
// ============================================================================

// Glow shader
const GLOW_VERTEX_SHADER = `
    varying vec3 vNormal;
    varying vec3 vViewPosition;

    void main() {
        vNormal = normalize(normalMatrix * normal);
        vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
        vViewPosition = -mvPosition.xyz;
        gl_Position = projectionMatrix * mvPosition;
    }
`;

const GLOW_FRAGMENT_SHADER = `
    uniform vec3 glowColor;
    uniform float glowIntensity;
    uniform float glowFalloff;
    uniform float time;
    uniform int glowType; // 0: outer, 1: inner, 2: pulsating

    varying vec3 vNormal;
    varying vec3 vViewPosition;

    void main() {
        vec3 viewDir = normalize(vViewPosition);
        float fresnel = 1.0 - abs(dot(viewDir, vNormal));

        float glow = pow(fresnel, glowFalloff) * glowIntensity;

        if (glowType == 1) {
            // Inner glow
            glow = pow(1.0 - fresnel, glowFalloff) * glowIntensity;
        } else if (glowType == 2) {
            // Pulsating glow
            float pulse = sin(time * 3.0) * 0.3 + 0.7;
            glow = pow(fresnel, glowFalloff) * glowIntensity * pulse;
        }

        vec3 finalColor = glowColor * glow;
        gl_FragColor = vec4(finalColor, glow);
    }
`;

// Holographic shader
const HOLOGRAPHIC_VERTEX_SHADER = `
    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;

    void main() {
        vUv = uv;
        vPosition = position;
        vNormal = normal;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const HOLOGRAPHIC_FRAGMENT_SHADER = `
    uniform vec3 color1;
    uniform vec3 color2;
    uniform float time;
    uniform float scanSpeed;
    uniform float noiseIntensity;
    uniform int holoType; // 0: scan, 1: glitch, 2: noise, 3: interference

    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;

    // Noise fonksiyonu
    float random(vec2 st) {
        return fract(sin(dot(st.xy, vec2(12.9898, 78.233))) * 43758.5453123);
    }

    float noise(vec2 st) {
        vec2 i = floor(st);
        vec2 f = fract(st);
        float a = random(i);
        float b = random(i + vec2(1.0, 0.0));
        float c = random(i + vec2(0.0, 1.0));
        float d = random(i + vec2(1.0, 1.0));
        vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(a, b, u.x) + (c - a) * u.y * (1.0 - u.x) + (d - b) * u.x * u.y;
    }

    void main() {
        vec3 finalColor = color1;
        float alpha = 0.8;

        if (holoType == 0) {
            // Scan line effect
            float scanLine = fract(vUv.y * 50.0 - time * scanSpeed);
            scanLine = smoothstep(0.0, 0.1, scanLine) * smoothstep(1.0, 0.9, scanLine);
            finalColor = mix(color1, color2, scanLine * 0.5);
            alpha = 0.6 + scanLine * 0.4;
        }
        else if (holoType == 1) {
            // Glitch effect
            float glitch = step(0.99, random(vec2(floor(time * 10.0), floor(vUv.y * 20.0))));
            vec2 offset = vec2(glitch * 0.1, 0.0);
            finalColor = mix(color1, color2, glitch);
            alpha = 0.7 + glitch * 0.3;
        }
        else if (holoType == 2) {
            // Noise effect
            float n = noise(vUv * 10.0 + time);
            finalColor = mix(color1, color2, n * noiseIntensity);
            alpha = 0.5 + n * 0.5;
        }
        else if (holoType == 3) {
            // Interference pattern
            float interference = sin(vUv.x * 100.0 + time) * sin(vUv.y * 100.0 - time);
            interference = interference * 0.5 + 0.5;
            finalColor = mix(color1, color2, interference);
            alpha = 0.6 + interference * 0.4;
        }

        gl_FragColor = vec4(finalColor, alpha);
    }
`;

// Energy field shader
const ENERGY_VERTEX_SHADER = `
    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;

    void main() {
        vUv = uv;
        vPosition = position;
        vNormal = normal;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
`;

const ENERGY_FRAGMENT_SHADER = `
    uniform vec3 energyColor;
    uniform float time;
    uniform float intensity;
    uniform int energyType; // 0: field, 1: plasma, 2: electric, 3: shield

    varying vec2 vUv;
    varying vec3 vPosition;
    varying vec3 vNormal;

    // Simplex noise fonksiyonu
    vec3 mod289(vec3 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 permute(vec4 x) { return mod289(((x * 34.0) + 1.0) * x); }
    vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }

    float snoise(vec3 v) {
        const vec2 C = vec2(1.0 / 6.0, 1.0 / 3.0);
        const vec4 D = vec4(0.0, 0.5, 1.0, 2.0);
        vec3 i = floor(v + dot(v, C.yyy));
        vec3 x0 = v - i + dot(i, C.xxx);
        vec3 g = step(x0.yzx, x0.xyz);
        vec3 l = 1.0 - g;
        vec3 i1 = min(g.xyz, l.zxy);
        vec3 i2 = max(g.xyz, l.zxy);
        vec3 x1 = x0 - i1 + C.xxx;
        vec3 x2 = x0 - i2 + C.yyy;
        vec3 x3 = x0 - D.yyy;
        i = mod289(i);
        vec4 p = permute(permute(permute(
                 i.z + vec4(0.0, i1.z, i2.z, 1.0))
               + i.y + vec4(0.0, i1.y, i2.y, 1.0))
               + i.x + vec4(0.0, i1.x, i2.x, 1.0));
        float n_ = 0.142857142857;
        vec3 ns = n_ * D.wyz - D.xzx;
        vec4 j = p - 49.0 * floor(p * ns.z * ns.z);
        vec4 x_ = floor(j * ns.z);
        vec4 y_ = floor(j - 7.0 * x_);
        vec4 x = x_ * ns.x + ns.yyyy;
        vec4 y = y_ * ns.x + ns.yyyy;
        vec4 h = 1.0 - abs(x) - abs(y);
        vec4 b0 = vec4(x.xy, y.xy);
        vec4 b1 = vec4(x.zw, y.zw);
        vec4 s0 = floor(b0) * 2.0 + 1.0;
        vec4 s1 = floor(b1) * 2.0 + 1.0;
        vec4 sh = -step(h, vec4(0.0));
        vec4 a0 = b0.xzyw + s0.xzyw * sh.xxyy;
        vec4 a1 = b1.xzyw + s1.xzyw * sh.zzww;
        vec3 p0 = vec3(a0.xy, h.x);
        vec3 p1 = vec3(a0.zw, h.y);
        vec3 p2 = vec3(a1.xy, h.z);
        vec3 p3 = vec3(a1.zw, h.w);
        vec4 norm = taylorInvSqrt(vec4(dot(p0, p0), dot(p1, p1), dot(p2, p2), dot(p3, p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;
        vec4 m = max(0.6 - vec4(dot(x0, x0), dot(x1, x1), dot(x2, x2), dot(x3, x3)), 0.0);
        m = m * m;
        return 42.0 * dot(m * m, vec4(dot(p0, x0), dot(p1, x1), dot(p2, x2), dot(p3, x3)));
    }

    void main() {
        vec3 finalColor = energyColor;
        float alpha = intensity;

        if (energyType == 0) {
            // Energy field
            float field = snoise(vec3(vUv * 5.0, time * 0.5));
            field = field * 0.5 + 0.5;
            finalColor = energyColor * (0.5 + field * 0.5);
            alpha = intensity * field;
        }
        else if (energyType == 1) {
            // Plasma
            float plasma = snoise(vec3(vUv * 3.0, time));
            plasma += snoise(vec3(vUv * 6.0, time * 1.5)) * 0.5;
            plasma += snoise(vec3(vUv * 12.0, time * 2.0)) * 0.25;
            plasma = plasma * 0.5 + 0.5;
            finalColor = mix(energyColor, energyColor * 2.0, plasma);
            alpha = intensity * (0.5 + plasma * 0.5);
        }
        else if (energyType == 2) {
            // Electric
            float electric = abs(snoise(vec3(vUv * 20.0, time * 5.0)));
            electric = pow(electric, 3.0);
            finalColor = energyColor * (1.0 + electric * 2.0);
            alpha = intensity * (0.3 + electric * 0.7);
        }
        else if (energyType == 3) {
            // Shield
            float shield = 1.0 - length(vUv - 0.5) * 2.0;
            shield = max(0.0, shield);
            float shimmer = snoise(vec3(vUv * 10.0, time)) * 0.2 + 0.8;
            finalColor = energyColor * shimmer;
            alpha = intensity * shield * shimmer;
        }

        gl_FragColor = vec4(finalColor, alpha);
    }
`;

// Outline shader
const OUTLINE_VERTEX_SHADER = `
    uniform float outlineThickness;

    void main() {
        vec3 newPosition = position + normal * outlineThickness;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(newPosition, 1.0);
    }
`;

const OUTLINE_FRAGMENT_SHADER = `
    uniform vec3 outlineColor;
    uniform float outlineOpacity;

    void main() {
        gl_FragColor = vec4(outlineColor, outlineOpacity);
    }
`;

// ============================================================================
// EASING FUNCTIONS
// ============================================================================

const EASING_FUNCTIONS: Record<EasingType, (t: number) => number> = {
    [EasingType.LINEAR]: (t) => t,

    [EasingType.EASE_IN]: (t) => t * t,

    [EasingType.EASE_OUT]: (t) => t * (2 - t),

    [EasingType.EASE_IN_OUT]: (t) => t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t,

    [EasingType.ELASTIC]: (t) => {
        if (t === 0 || t === 1) return t;
        return Math.pow(2, -10 * t) * Math.sin((t - 0.1) * 5 * Math.PI) + 1;
    },

    [EasingType.BOUNCE]: (t) => {
        if (t < 1 / 2.75) {
            return 7.5625 * t * t;
        } else if (t < 2 / 2.75) {
            return 7.5625 * (t -= 1.5 / 2.75) * t + 0.75;
        } else if (t < 2.5 / 2.75) {
            return 7.5625 * (t -= 2.25 / 2.75) * t + 0.9375;
        } else {
            return 7.5625 * (t -= 2.625 / 2.75) * t + 0.984375;
        }
    },

    [EasingType.BACK]: (t) => {
        const c1 = 1.70158;
        const c3 = c1 + 1;
        return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
    },

    [EasingType.SINE]: (t) => 1 - Math.cos((t * Math.PI) / 2),

    [EasingType.EXPO]: (t) => t === 0 ? 0 : Math.pow(2, 10 * (t - 1)),

    [EasingType.CIRC]: (t) => 1 - Math.sqrt(1 - Math.pow(t, 2))
};

// ============================================================================
// EFFECT CLASSES
// ============================================================================

/**
 * Base Effect sınıfı
 */
export abstract class BaseEffect {
    protected config: EffectConfig;
    protected state: EffectState;
    protected target: THREE.Object3D | null = null;
    protected animationId: number = 0;
    protected startTime: number = 0;

    constructor(config: Partial<EffectConfig> = {}) {
        this.config = { ...DEFAULT_EFFECT_CONFIG, ...config };
        this.state = {
            isPlaying: false,
            isPaused: false,
            progress: 0,
            elapsedTime: 0
        };
    }

    public abstract apply(target: THREE.Object3D): void;
    public abstract update(deltaTime: number): void;
    public abstract dispose(): void;

    public play(): void {
        this.state.isPlaying = true;
        this.state.isPaused = false;
        this.startTime = performance.now();
        this.animate();
    }

    public pause(): void {
        this.state.isPaused = true;
    }

    public resume(): void {
        this.state.isPaused = false;
        this.animate();
    }

    public stop(): void {
        this.state.isPlaying = false;
        this.state.isPaused = false;
        this.state.progress = 0;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = 0;
        }
    }

    public setIntensity(intensity: number): void {
        this.config.intensity = THREE.MathUtils.clamp(intensity, 0, 1);
    }

    public setColor(color: number): void {
        this.config.color = color;
    }

    public getState(): EffectState {
        return { ...this.state };
    }

    protected animate = (): void => {
        if (!this.state.isPlaying || this.state.isPaused) return;

        const elapsed = performance.now() - this.startTime;
        this.state.elapsedTime = elapsed;

        const t = (elapsed % this.config.duration) / this.config.duration;
        this.state.progress = EASING_FUNCTIONS[this.config.easing](t);

        this.update(elapsed / 1000);

        if (this.config.loop || elapsed < this.config.duration) {
            this.animationId = requestAnimationFrame(this.animate);
        } else {
            this.state.isPlaying = false;
        }
    };
}

/**
 * Glow Effect
 */
export class GlowEffect extends BaseEffect {
    private glowMesh: THREE.Mesh | null = null;
    private glowMaterial: THREE.ShaderMaterial | null = null;

    constructor(config: Partial<EffectConfig> = {}) {
        super({ ...config, type: config.type || EffectType.GLOW_OUTER });
    }

    public apply(target: THREE.Object3D): void {
        this.target = target;

        if (!(target instanceof THREE.Mesh)) return;

        // Glow tipi
        let glowType = 0;
        if (this.config.type === EffectType.GLOW_INNER) glowType = 1;
        if (this.config.type === EffectType.GLOW_PULSATING) glowType = 2;

        // Glow material
        this.glowMaterial = new THREE.ShaderMaterial({
            uniforms: {
                glowColor: { value: new THREE.Color(this.config.color) },
                glowIntensity: { value: this.config.intensity },
                glowFalloff: { value: this.config.glowFalloff || 2 },
                time: { value: 0 },
                glowType: { value: glowType }
            },
            vertexShader: GLOW_VERTEX_SHADER,
            fragmentShader: GLOW_FRAGMENT_SHADER,
            transparent: true,
            blending: THREE.AdditiveBlending,
            side: this.config.type === EffectType.GLOW_INNER ? THREE.BackSide : THREE.FrontSide,
            depthWrite: false
        });

        // Glow mesh
        const geometry = (target as THREE.Mesh).geometry.clone();
        this.glowMesh = new THREE.Mesh(geometry, this.glowMaterial);

        // Scale up for outer glow
        if (this.config.type !== EffectType.GLOW_INNER) {
            this.glowMesh.scale.multiplyScalar(1 + (this.config.glowRadius || 0.1));
        }

        target.add(this.glowMesh);
    }

    public update(deltaTime: number): void {
        if (!this.glowMaterial) return;

        this.glowMaterial.uniforms.time.value = this.state.elapsedTime / 1000;

        if (this.config.type === EffectType.GLOW_RAINBOW) {
            const hue = (this.state.elapsedTime / 1000) % 1;
            const color = new THREE.Color().setHSL(hue, 1, 0.5);
            this.glowMaterial.uniforms.glowColor.value = color;
        }
    }

    public dispose(): void {
        this.stop();

        if (this.glowMesh) {
            if (this.glowMesh.geometry) this.glowMesh.geometry.dispose();
            if (this.target) this.target.remove(this.glowMesh);
            this.glowMesh = null;
        }

        if (this.glowMaterial) {
            this.glowMaterial.dispose();
            this.glowMaterial = null;
        }
    }
}

/**
 * Pulse Effect
 */
export class PulseEffect extends BaseEffect {
    private originalScale: THREE.Vector3 = new THREE.Vector3(1, 1, 1);
    private originalOpacity: number = 1;
    private originalColor: THREE.Color = new THREE.Color(0xffffff);

    constructor(config: Partial<EffectConfig> = {}) {
        super({ ...config, type: config.type || EffectType.PULSE_SCALE });
    }

    public apply(target: THREE.Object3D): void {
        this.target = target;
        this.originalScale.copy(target.scale);

        if (target instanceof THREE.Mesh && target.material) {
            const mat = target.material as THREE.MeshBasicMaterial;
            if (mat.opacity !== undefined) this.originalOpacity = mat.opacity;
            if (mat.color) this.originalColor.copy(mat.color);
        }
    }

    public update(deltaTime: number): void {
        if (!this.target) return;

        const t = this.state.progress;
        const intensity = this.config.intensity;

        switch (this.config.type) {
            case EffectType.PULSE_SCALE:
                const minScale = this.config.minScale || 0.9;
                const maxScale = this.config.maxScale || 1.1;
                const scale = minScale + (maxScale - minScale) * Math.sin(t * Math.PI * 2) * 0.5 + 0.5;
                this.target.scale.copy(this.originalScale).multiplyScalar(scale);
                break;

            case EffectType.PULSE_OPACITY:
                if (this.target instanceof THREE.Mesh && this.target.material) {
                    const mat = this.target.material as THREE.MeshBasicMaterial;
                    const minOpacity = this.config.minIntensity || 0.3;
                    const maxOpacity = this.config.maxIntensity || 1;
                    mat.opacity = minOpacity + (maxOpacity - minOpacity) * (Math.sin(t * Math.PI * 2) * 0.5 + 0.5);
                    mat.transparent = true;
                    mat.needsUpdate = true;
                }
                break;

            case EffectType.PULSE_COLOR:
                if (this.target instanceof THREE.Mesh && this.target.material) {
                    const mat = this.target.material as THREE.MeshBasicMaterial;
                    const color1 = new THREE.Color(this.config.color);
                    const color2 = new THREE.Color(this.config.secondaryColor || 0xffffff);
                    const blend = Math.sin(t * Math.PI * 2) * 0.5 + 0.5;
                    mat.color.copy(color1).lerp(color2, blend);
                    mat.needsUpdate = true;
                }
                break;

            case EffectType.PULSE_BRIGHTNESS:
                if (this.target instanceof THREE.Mesh && this.target.material) {
                    const mat = this.target.material as THREE.MeshBasicMaterial;
                    const brightness = 0.5 + Math.sin(t * Math.PI * 2) * 0.5 * intensity;
                    mat.color.copy(this.originalColor).multiplyScalar(brightness + 0.5);
                    mat.needsUpdate = true;
                }
                break;
        }
    }

    public dispose(): void {
        this.stop();

        if (this.target) {
            this.target.scale.copy(this.originalScale);

            if (this.target instanceof THREE.Mesh && this.target.material) {
                const mat = this.target.material as THREE.MeshBasicMaterial;
                mat.opacity = this.originalOpacity;
                mat.color.copy(this.originalColor);
                mat.needsUpdate = true;
            }
        }
    }
}

/**
 * Trail Effect
 */
export class TrailEffect extends BaseEffect {
    private trailPoints: THREE.Vector3[] = [];
    private trailLine: THREE.Line | null = null;
    private trailMaterial: THREE.LineBasicMaterial | null = null;
    private lastPosition: THREE.Vector3 = new THREE.Vector3();

    constructor(config: Partial<EffectConfig> = {}) {
        super({ ...config, type: config.type || EffectType.TRAIL_FADE });
    }

    public apply(target: THREE.Object3D): void {
        this.target = target;
        this.lastPosition.copy(target.position);

        // Trail material
        this.trailMaterial = new THREE.LineBasicMaterial({
            color: this.config.color,
            opacity: this.config.opacity,
            transparent: true,
            linewidth: 2
        });

        // Trail geometry (başlangıçta boş)
        const geometry = new THREE.BufferGeometry();
        this.trailLine = new THREE.Line(geometry, this.trailMaterial);

        // Scene'e ekle (parent'a değil, çünkü trail dünya koordinatlarında)
        if (target.parent) {
            target.parent.add(this.trailLine);
        }
    }

    public update(deltaTime: number): void {
        if (!this.target || !this.trailLine) return;

        const currentPosition = this.target.position.clone();

        // Pozisyon değiştiyse trail'e ekle
        if (!currentPosition.equals(this.lastPosition)) {
            this.trailPoints.push(currentPosition.clone());

            // Trail uzunluğunu sınırla
            const maxLength = this.config.trailLength || 10;
            while (this.trailPoints.length > maxLength) {
                this.trailPoints.shift();
            }

            this.lastPosition.copy(currentPosition);
        }

        // Trail geometry güncelle
        if (this.trailPoints.length >= 2) {
            const positions: number[] = [];
            const colors: number[] = [];
            const color = new THREE.Color(this.config.color);

            this.trailPoints.forEach((point, i) => {
                positions.push(point.x, point.y, point.z);

                // Fade efekti için renk
                const alpha = i / this.trailPoints.length;
                const fadedColor = color.clone().multiplyScalar(alpha);
                colors.push(fadedColor.r, fadedColor.g, fadedColor.b);
            });

            const geometry = new THREE.BufferGeometry();
            geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
            geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

            // Eski geometry'yi dispose et
            if (this.trailLine.geometry) {
                this.trailLine.geometry.dispose();
            }

            this.trailLine.geometry = geometry;

            if (this.trailMaterial) {
                this.trailMaterial.vertexColors = true;
                this.trailMaterial.needsUpdate = true;
            }
        }

        // Decay uygula
        if (this.config.type === EffectType.TRAIL_FADE) {
            const decay = this.config.trailDecay || 0.95;
            // Trail noktalarını yavaşça sil
            if (Math.random() > decay) {
                this.trailPoints.shift();
            }
        }
    }

    public dispose(): void {
        this.stop();

        if (this.trailLine) {
            if (this.trailLine.geometry) this.trailLine.geometry.dispose();
            if (this.trailLine.parent) this.trailLine.parent.remove(this.trailLine);
            this.trailLine = null;
        }

        if (this.trailMaterial) {
            this.trailMaterial.dispose();
            this.trailMaterial = null;
        }

        this.trailPoints = [];
    }
}

/**
 * Particle Effect
 */
export class ParticleEffect extends BaseEffect {
    private particles: THREE.Points | null = null;
    private particleGeometry: THREE.BufferGeometry | null = null;
    private particleMaterial: THREE.PointsMaterial | null = null;
    private velocities: THREE.Vector3[] = [];

    constructor(config: Partial<EffectConfig> = {}) {
        super({ ...config, type: config.type || EffectType.PARTICLE_SPARK });
    }

    public apply(target: THREE.Object3D): void {
        this.target = target;

        const count = this.config.particleCount || 100;
        const positions = new Float32Array(count * 3);
        const colors = new Float32Array(count * 3);
        const color = new THREE.Color(this.config.color);

        this.velocities = [];

        for (let i = 0; i < count; i++) {
            // Başlangıç pozisyonu (target etrafında)
            positions[i * 3] = (Math.random() - 0.5) * 0.5;
            positions[i * 3 + 1] = (Math.random() - 0.5) * 0.5;
            positions[i * 3 + 2] = (Math.random() - 0.5) * 0.5;

            // Renk
            const particleColor = color.clone();
            if (this.config.type === EffectType.PARTICLE_FIRE) {
                particleColor.setHSL(Math.random() * 0.1, 1, 0.5);
            }
            colors[i * 3] = particleColor.r;
            colors[i * 3 + 1] = particleColor.g;
            colors[i * 3 + 2] = particleColor.b;

            // Velocity
            const velocity = new THREE.Vector3(
                (Math.random() - 0.5) * (this.config.particleSpeed || 1),
                (Math.random() - 0.5) * (this.config.particleSpeed || 1),
                (Math.random() - 0.5) * (this.config.particleSpeed || 1)
            );

            // Tip bazlı velocity ayarı
            if (this.config.type === EffectType.PARTICLE_FIRE) {
                velocity.y = Math.abs(velocity.y) * 2; // Yukarı doğru
            } else if (this.config.type === EffectType.PARTICLE_SNOW) {
                velocity.y = -Math.abs(velocity.y) * 0.5; // Aşağı doğru
                velocity.x *= 0.2;
                velocity.z *= 0.2;
            }

            this.velocities.push(velocity);
        }

        this.particleGeometry = new THREE.BufferGeometry();
        this.particleGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
        this.particleGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

        this.particleMaterial = new THREE.PointsMaterial({
            size: this.config.particleSize || 0.02,
            vertexColors: true,
            transparent: true,
            opacity: this.config.opacity,
            blending: THREE.AdditiveBlending,
            depthWrite: false
        });

        this.particles = new THREE.Points(this.particleGeometry, this.particleMaterial);
        target.add(this.particles);
    }

    public update(deltaTime: number): void {
        if (!this.particles || !this.particleGeometry) return;

        const positions = this.particleGeometry.attributes.position.array as Float32Array;
        const count = positions.length / 3;

        for (let i = 0; i < count; i++) {
            const velocity = this.velocities[i];

            // Pozisyon güncelle
            positions[i * 3] += velocity.x * deltaTime;
            positions[i * 3 + 1] += velocity.y * deltaTime;
            positions[i * 3 + 2] += velocity.z * deltaTime;

            // Sınır kontrolü - reset
            const dist = Math.sqrt(
                positions[i * 3] ** 2 +
                positions[i * 3 + 1] ** 2 +
                positions[i * 3 + 2] ** 2
            );

            if (dist > 1) {
                positions[i * 3] = (Math.random() - 0.5) * 0.1;
                positions[i * 3 + 1] = (Math.random() - 0.5) * 0.1;
                positions[i * 3 + 2] = (Math.random() - 0.5) * 0.1;
            }
        }

        this.particleGeometry.attributes.position.needsUpdate = true;
    }

    public dispose(): void {
        this.stop();

        if (this.particles) {
            if (this.target) this.target.remove(this.particles);
            this.particles = null;
        }

        if (this.particleGeometry) {
            this.particleGeometry.dispose();
            this.particleGeometry = null;
        }

        if (this.particleMaterial) {
            this.particleMaterial.dispose();
            this.particleMaterial = null;
        }

        this.velocities = [];
    }
}

/**
 * Holographic Effect
 */
export class HolographicEffect extends BaseEffect {
    private holoMesh: THREE.Mesh | null = null;
    private holoMaterial: THREE.ShaderMaterial | null = null;

    constructor(config: Partial<EffectConfig> = {}) {
        super({ ...config, type: config.type || EffectType.HOLOGRAPHIC_SCAN });
    }

    public apply(target: THREE.Object3D): void {
        this.target = target;

        if (!(target instanceof THREE.Mesh)) return;

        // Holo tipi
        let holoType = 0;
        if (this.config.type === EffectType.HOLOGRAPHIC_GLITCH) holoType = 1;
        if (this.config.type === EffectType.HOLOGRAPHIC_NOISE) holoType = 2;
        if (this.config.type === EffectType.HOLOGRAPHIC_INTERFERENCE) holoType = 3;

        // Holo material
        this.holoMaterial = new THREE.ShaderMaterial({
            uniforms: {
                color1: { value: new THREE.Color(this.config.color) },
                color2: { value: new THREE.Color(this.config.secondaryColor || 0x00ffff) },
                time: { value: 0 },
                scanSpeed: { value: 2 },
                noiseIntensity: { value: this.config.intensity },
                holoType: { value: holoType }
            },
            vertexShader: HOLOGRAPHIC_VERTEX_SHADER,
            fragmentShader: HOLOGRAPHIC_FRAGMENT_SHADER,
            transparent: true,
            side: THREE.DoubleSide,
            depthWrite: false
        });

        // Holo mesh
        const geometry = (target as THREE.Mesh).geometry.clone();
        this.holoMesh = new THREE.Mesh(geometry, this.holoMaterial);
        this.holoMesh.scale.multiplyScalar(1.01); // Hafif büyük

        target.add(this.holoMesh);
    }

    public update(deltaTime: number): void {
        if (!this.holoMaterial) return;
        this.holoMaterial.uniforms.time.value = this.state.elapsedTime / 1000;
    }

    public dispose(): void {
        this.stop();

        if (this.holoMesh) {
            if (this.holoMesh.geometry) this.holoMesh.geometry.dispose();
            if (this.target) this.target.remove(this.holoMesh);
            this.holoMesh = null;
        }

        if (this.holoMaterial) {
            this.holoMaterial.dispose();
            this.holoMaterial = null;
        }
    }
}

/**
 * Energy Field Effect
 */
export class EnergyFieldEffect extends BaseEffect {
    private energyMesh: THREE.Mesh | null = null;
    private energyMaterial: THREE.ShaderMaterial | null = null;

    constructor(config: Partial<EffectConfig> = {}) {
        super({ ...config, type: config.type || EffectType.ENERGY_FIELD });
    }

    public apply(target: THREE.Object3D): void {
        this.target = target;

        if (!(target instanceof THREE.Mesh)) return;

        // Energy tipi
        let energyType = 0;
        if (this.config.type === EffectType.ENERGY_PLASMA) energyType = 1;
        if (this.config.type === EffectType.ENERGY_ELECTRIC) energyType = 2;
        if (this.config.type === EffectType.ENERGY_SHIELD) energyType = 3;

        // Energy material
        this.energyMaterial = new THREE.ShaderMaterial({
            uniforms: {
                energyColor: { value: new THREE.Color(this.config.color) },
                time: { value: 0 },
                intensity: { value: this.config.intensity },
                energyType: { value: energyType }
            },
            vertexShader: ENERGY_VERTEX_SHADER,
            fragmentShader: ENERGY_FRAGMENT_SHADER,
            transparent: true,
            blending: THREE.AdditiveBlending,
            side: THREE.DoubleSide,
            depthWrite: false
        });

        // Energy mesh
        const geometry = (target as THREE.Mesh).geometry.clone();
        this.energyMesh = new THREE.Mesh(geometry, this.energyMaterial);
        this.energyMesh.scale.multiplyScalar(1.05);

        target.add(this.energyMesh);
    }

    public update(deltaTime: number): void {
        if (!this.energyMaterial) return;
        this.energyMaterial.uniforms.time.value = this.state.elapsedTime / 1000;
    }

    public dispose(): void {
        this.stop();

        if (this.energyMesh) {
            if (this.energyMesh.geometry) this.energyMesh.geometry.dispose();
            if (this.target) this.target.remove(this.energyMesh);
            this.energyMesh = null;
        }

        if (this.energyMaterial) {
            this.energyMaterial.dispose();
            this.energyMaterial = null;
        }
    }
}

/**
 * Outline Effect
 */
export class OutlineEffect extends BaseEffect {
    private outlineMesh: THREE.Mesh | null = null;
    private outlineMaterial: THREE.ShaderMaterial | null = null;

    constructor(config: Partial<EffectConfig> = {}) {
        super({ ...config, type: config.type || EffectType.HIGHLIGHT_OUTLINE });
    }

    public apply(target: THREE.Object3D): void {
        this.target = target;

        if (!(target instanceof THREE.Mesh)) return;

        // Outline material
        this.outlineMaterial = new THREE.ShaderMaterial({
            uniforms: {
                outlineThickness: { value: this.config.scale * 0.02 },
                outlineColor: { value: new THREE.Color(this.config.color) },
                outlineOpacity: { value: this.config.opacity }
            },
            vertexShader: OUTLINE_VERTEX_SHADER,
            fragmentShader: OUTLINE_FRAGMENT_SHADER,
            transparent: true,
            side: THREE.BackSide
        });

        // Outline mesh
        const geometry = (target as THREE.Mesh).geometry.clone();
        this.outlineMesh = new THREE.Mesh(geometry, this.outlineMaterial);

        target.add(this.outlineMesh);
    }

    public update(deltaTime: number): void {
        if (!this.outlineMaterial) return;

        // Pulsating outline
        if (this.config.loop) {
            const pulse = Math.sin(this.state.elapsedTime / 500) * 0.5 + 0.5;
            this.outlineMaterial.uniforms.outlineThickness.value =
                this.config.scale * 0.02 * (0.8 + pulse * 0.4);
        }
    }

    public dispose(): void {
        this.stop();

        if (this.outlineMesh) {
            if (this.outlineMesh.geometry) this.outlineMesh.geometry.dispose();
            if (this.target) this.target.remove(this.outlineMesh);
            this.outlineMesh = null;
        }

        if (this.outlineMaterial) {
            this.outlineMaterial.dispose();
            this.outlineMaterial = null;
        }
    }
}

// ============================================================================
// EFFECT MANAGER
// ============================================================================

/**
 * FikoDimensionEffects - Effect Manager
 */
export class FikoDimensionEffects {
    private effects: Map<string, BaseEffect> = new Map();
    private animationId: number = 0;
    private isRunning: boolean = false;
    private lastTime: number = 0;

    constructor() {
        // Singleton pattern değil, instance bazlı
    }

    /**
     * Efekt oluştur
     */
    public createEffect(type: EffectType, config: Partial<EffectConfig> = {}): BaseEffect {
        const effectConfig = { ...config, type };

        switch (type) {
            case EffectType.GLOW_INNER:
            case EffectType.GLOW_OUTER:
            case EffectType.GLOW_PULSATING:
            case EffectType.GLOW_RAINBOW:
                return new GlowEffect(effectConfig);

            case EffectType.PULSE_SCALE:
            case EffectType.PULSE_OPACITY:
            case EffectType.PULSE_COLOR:
            case EffectType.PULSE_BRIGHTNESS:
                return new PulseEffect(effectConfig);

            case EffectType.TRAIL_FADE:
            case EffectType.TRAIL_PARTICLE:
            case EffectType.TRAIL_RIBBON:
            case EffectType.TRAIL_COMET:
                return new TrailEffect(effectConfig);

            case EffectType.PARTICLE_SPARK:
            case EffectType.PARTICLE_DUST:
            case EffectType.PARTICLE_FIRE:
            case EffectType.PARTICLE_SNOW:
            case EffectType.PARTICLE_ENERGY:
                return new ParticleEffect(effectConfig);

            case EffectType.HOLOGRAPHIC_SCAN:
            case EffectType.HOLOGRAPHIC_GLITCH:
            case EffectType.HOLOGRAPHIC_NOISE:
            case EffectType.HOLOGRAPHIC_INTERFERENCE:
                return new HolographicEffect(effectConfig);

            case EffectType.ENERGY_FIELD:
            case EffectType.ENERGY_PLASMA:
            case EffectType.ENERGY_ELECTRIC:
            case EffectType.ENERGY_SHIELD:
                return new EnergyFieldEffect(effectConfig);

            case EffectType.HIGHLIGHT_OUTLINE:
            case EffectType.HIGHLIGHT_FILL:
            case EffectType.HIGHLIGHT_XRAY:
                return new OutlineEffect(effectConfig);

            default:
                return new GlowEffect(effectConfig);
        }
    }

    /**
     * Efekt ekle ve başlat
     */
    public addEffect(id: string, effect: BaseEffect, target: THREE.Object3D): void {
        // Var olan efekti temizle
        if (this.effects.has(id)) {
            this.removeEffect(id);
        }

        effect.apply(target);
        effect.play();
        this.effects.set(id, effect);

        if (!this.isRunning) {
            this.startUpdateLoop();
        }
    }

    /**
     * Efekt kaldır
     */
    public removeEffect(id: string): void {
        const effect = this.effects.get(id);
        if (effect) {
            effect.dispose();
            this.effects.delete(id);
        }

        if (this.effects.size === 0) {
            this.stopUpdateLoop();
        }
    }

    /**
     * Tüm efektleri kaldır
     */
    public removeAllEffects(): void {
        this.effects.forEach(effect => effect.dispose());
        this.effects.clear();
        this.stopUpdateLoop();
    }

    /**
     * Efekt al
     */
    public getEffect(id: string): BaseEffect | undefined {
        return this.effects.get(id);
    }

    /**
     * Efekt var mı kontrol et
     */
    public hasEffect(id: string): boolean {
        return this.effects.has(id);
    }

    /**
     * Tüm efektleri durdur
     */
    public pauseAll(): void {
        this.effects.forEach(effect => effect.pause());
    }

    /**
     * Tüm efektleri devam ettir
     */
    public resumeAll(): void {
        this.effects.forEach(effect => effect.resume());
    }

    /**
     * Update loop başlat
     */
    private startUpdateLoop(): void {
        if (this.isRunning) return;

        this.isRunning = true;
        this.lastTime = performance.now();
        this.update();
    }

    /**
     * Update loop durdur
     */
    private stopUpdateLoop(): void {
        this.isRunning = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = 0;
        }
    }

    /**
     * Update döngüsü
     */
    private update = (): void => {
        if (!this.isRunning) return;

        const currentTime = performance.now();
        const deltaTime = (currentTime - this.lastTime) / 1000;
        this.lastTime = currentTime;

        this.effects.forEach(effect => {
            const state = effect.getState();
            if (state.isPlaying && !state.isPaused) {
                effect.update(deltaTime);
            }
        });

        this.animationId = requestAnimationFrame(this.update);
    };

    /**
     * Temizlik
     */
    public dispose(): void {
        this.removeAllEffects();
    }
}

// ============================================================================
// EXPORTS
// ============================================================================

export {
    DEFAULT_EFFECT_CONFIG,
    EASING_FUNCTIONS
};
